import { useState, useEffect } from "react";
import * as Sentry from "@sentry/node";
import Link from "next/link";
import { useAccount } from "@hooks/useAccount";
import { AccountPageLayout } from "../AccountPageLayout";
import { Order } from "../OrderHistory/Order";
import { Loading } from "../Loading";
import { formattedPrice } from "../lib/textFormatters";
import { formatPrice } from "@components/Product/lib/format-variant-price";
import styles from "./styles/OrderDetails.module.css";

const Address = ({ title, name, address }) => {
  if (!address) {
    return null;
  }

  const { address1, address2, city, provinceCode, zip, country } = address;

  return (
    <address className={styles.address}>
      <p className={styles.title}>{title}</p>
      <p>{name}</p>
      <p>{address1}</p>
      {address2 && <p>{address2}</p>}
      <p>
        {city} {provinceCode} {zip}
      </p>
      <p>{country}</p>
    </address>
  );
};

const Invoice = ({ order, paymentDetails }) => {
  const { subtotalPriceV2, totalShippingPriceV2, totalTaxV2, totalPriceV2 } = order;
  const { currencyCode } = totalPriceV2;
  const formattedTotal = formatPrice({ amount: totalPriceV2.amount, currencyCode }, 1);

  const taxPercent = paymentDetails && paymentDetails.taxLines[0].ratePercentage;
  const cardNumber = paymentDetails && paymentDetails.transactions[0].accountNumber;
  const lastDigits = cardNumber && cardNumber.slice(-4);

  return (
    <div className={styles.invoiceContainer}>
      <p className={`${styles.title} ${styles.invoiceTitle}`}>Invoice</p>
      <ul>
        <li className={styles.lineItem}>
          <p>Bag Subtotal</p>
          <p>{formattedPrice(subtotalPriceV2.amount)}</p>
        </li>
        <li className={styles.lineItem}>
          <p>Shipping</p>
          <p>{formattedPrice(totalShippingPriceV2.amount)}</p>
        </li>
        <li className={styles.lineItem}>
          <p>Tax ({taxPercent}%)</p>
          <p>{formattedPrice(totalTaxV2.amount)}</p>
        </li>
        <li className={styles.lineItem}>
          <p>Total {lastDigits && `(Card *${lastDigits})`}</p>
          <p>{formattedTotal}</p>
        </li>
      </ul>
    </div>
  );
};

export const OrderDetails = ({ id }) => {
  const [paymentDetails, setPaymentDetails] = useState();
  const [order, setOrder] = useState();
  const { account } = useAccount();
  const { orders, customer } = account;

  // Set order
  useEffect(() => {
    if (orders) {
      const order = orders.find((order) => order.id === id);
      setOrder(order);
    }
  }, [orders]);

  // Get order payment info from admin API
  useEffect(() => {
    const getPaymentDetails = async () => {
      try {
        const response = await fetch(`/api/orders/${order.id}`, {
          headers: { Accept: "application/json" },
        });

        const data = await response.json();

        setPaymentDetails(data);
      } catch (error) {
        Sentry.captureException(error);
        res.status(422).json({ error: true, message: String(error) });
      }
    };

    if (order) getPaymentDetails();
  }, [order]);

  const Details = () => {
    return (
      <section className={styles.details}>
        {paymentDetails && (
          <Address
            title="Billing Address"
            name={paymentDetails.billingAddress?.name}
            address={paymentDetails.billingAddress}
          />
        )}
        {order.shippingAddress && (
          <Address
            title="Shipping Address"
            name={customer.displayName}
            address={order.shippingAddress}
          />
        )}
        <Invoice order={order} paymentDetails={paymentDetails} />
      </section>
    );
  };

  const Actions = () => {
    return (
      <section className={styles.actions}>
        <Link href="https://returns.kotn.com">
          <a className={`${styles.button} ${styles.buttonLight}`} target="_blank">
            Exchange or Return
          </a>
        </Link>
        <Link href={order.statusUrl}>
          <a className={`${styles.button} ${styles.buttonDark}`} target="_blank">
            Track Order
          </a>
        </Link>
      </section>
    );
  };

  if (!order) return <Loading title="Order Details" />;

  return (
    <AccountPageLayout title={`Order Details: ${order.orderNumber}`}>
      <section className={styles.container}>
        <Order order={order} />
        <Details />
        <Actions />
      </section>
    </AccountPageLayout>
  );
};
